import React, { useCallback, useState, useRef } from 'react'

interface DualRangeSliderProps {
  min: number
  max: number
  value: [number, number]
  onChange: (value: [number, number]) => void
  step?: number
  className?: string
  label?: string
}

export function DualRangeSlider({
  min,
  max,
  value,
  onChange,
  step = 1,
  className = '',
  label
}: DualRangeSliderProps) {
  const [minVal, maxVal] = value
  const [activeSlider, setActiveSlider] = useState<'min' | 'max' | null>(null)
  const sliderRef = useRef<HTMLDivElement>(null)

  // Calculate which slider should handle the interaction based on position
  const getSliderFromPosition = useCallback((clientX: number) => {
    if (!sliderRef.current) return 'min'
    
    const rect = sliderRef.current.getBoundingClientRect()
    const position = (clientX - rect.left) / rect.width
    const positionValue = min + position * (max - min)
    
    // Clamp position value to valid range
    const clampedValue = Math.max(min, Math.min(max, positionValue))
    
    // Calculate distances to current values
    const distanceToMin = Math.abs(clampedValue - minVal)
    const distanceToMax = Math.abs(clampedValue - maxVal)
    
    // Handle edge cases
    if (clampedValue <= minVal + step) return 'min'
    if (clampedValue >= maxVal - step) return 'max'
    
    // Return closest slider, with slight bias toward min for equal distances
    return distanceToMin <= distanceToMax ? 'min' : 'max'
  }, [min, max, minVal, maxVal, step])

  // Smart z-index calculation - default priority to left slider
  const getSliderZIndex = useCallback((sliderType: 'min' | 'max') => {
    if (activeSlider === sliderType) return 10 // Active slider on top
    
    // Default: Left (min) slider gets higher z-index to solve accessibility issue
    if (sliderType === 'min') return 4
    if (sliderType === 'max') return 2
    
    // When sliders are very close, prioritize based on position in range
    const range = max - min
    const threshold = Math.max(step, range * 0.01) // 1% threshold or step size
    
    if (Math.abs(maxVal - minVal) <= threshold) {
      // If in lower half of range, prioritize min slider
      const midpoint = min + range / 2
      const avgValue = (minVal + maxVal) / 2
      return avgValue < midpoint 
        ? (sliderType === 'min' ? 6 : 3)
        : (sliderType === 'max' ? 6 : 3)
    }
    
    return sliderType === 'min' ? 4 : 2
  }, [activeSlider, min, max, minVal, maxVal, step])

  // Enhanced event handling with position awareness
  const handleSliderInteraction = useCallback((event: React.MouseEvent | React.TouchEvent) => {
    const clientX = 'touches' in event ? event.touches[0]?.clientX : event.clientX
    if (clientX === undefined) return
    
    const targetSlider = getSliderFromPosition(clientX)
    setActiveSlider(targetSlider)
    
    return targetSlider
  }, [getSliderFromPosition])

  const handleMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newMin = Number(e.target.value)
      const clampedMin = Math.max(min, Math.min(newMin, maxVal))
      onChange([clampedMin, maxVal])
    },
    [min, maxVal, onChange]
  )

  const handleMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newMax = Number(e.target.value)
      const clampedMax = Math.max(minVal, Math.min(newMax, max))
      onChange([minVal, clampedMax])
    },
    [minVal, max, onChange]
  )

  // Calculate percentages for visual styling
  const minPercent = ((minVal - min) / (max - min)) * 100
  const maxPercent = ((maxVal - min) / (max - min)) * 100

  return (
    <div className={`space-y-3 ${className}`}>
      {label && (
        <label className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide block">
          {label}: {minVal} - {maxVal}
        </label>
      )}
      
      <div className="px-3 py-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div 
          ref={sliderRef}
          className="relative h-6"
          onMouseDown={handleSliderInteraction}
          onTouchStart={handleSliderInteraction}
        >
          {/* Track background */}
          <div className="absolute w-full h-2 top-2 bg-gray-200 dark:bg-gray-600 rounded-lg" />
          
          {/* Active range */}
          <div
            className="absolute h-2 top-2 bg-blue-600 rounded-lg"
            style={{
              left: `${minPercent}%`,
              width: `${maxPercent - minPercent}%`
            }}
          />
          
          {/* Min range input */}
          <input
            type="range"
            min={min}
            max={max}
            value={minVal}
            step={step}
            onChange={handleMinChange}
            onMouseDown={() => setActiveSlider('min')}
            onTouchStart={() => setActiveSlider('min')}
            onMouseUp={() => setActiveSlider(null)}
            onTouchEnd={() => setActiveSlider(null)}
            aria-label={`Minimum value: ${minVal}`}
            className={`absolute w-full h-6 bg-transparent appearance-none cursor-pointer slider-thumb ${
              activeSlider === 'min' ? 'slider-thumb--active' : ''
            }`}
            style={{ 
              zIndex: getSliderZIndex('min'),
              pointerEvents: 'auto',
              WebkitAppearance: 'none',
              background: 'transparent'
            }}
          />
          
          {/* Max range input */}
          <input
            type="range"
            min={min}
            max={max}
            value={maxVal}
            step={step}
            onChange={handleMaxChange}
            onMouseDown={() => setActiveSlider('max')}
            onTouchStart={() => setActiveSlider('max')}
            onMouseUp={() => setActiveSlider(null)}
            onTouchEnd={() => setActiveSlider(null)}
            aria-label={`Maximum value: ${maxVal}`}
            className={`absolute w-full h-6 bg-transparent appearance-none cursor-pointer slider-thumb ${
              activeSlider === 'max' ? 'slider-thumb--active' : ''
            }`}
            style={{ 
              zIndex: getSliderZIndex('max'),
              pointerEvents: 'auto',
              WebkitAppearance: 'none',
              background: 'transparent'
            }}
          />
        </div>
        
        {/* Range labels */}
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-3">
          <span>{min}</span>
          <span>{max}</span>
        </div>
      </div>
    </div>
  )
}